import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, Eye, X, Download, Loader2 } from 'lucide-react';
import { uploadFile } from '../../api/chatApi';

declare const window: Window & { Telegram?: any; __API_BASE__?: string };

const LANG_MAP: Record<string, string> = {
  js: 'JavaScript', javascript: 'JavaScript', ts: 'TypeScript', typescript: 'TypeScript',
  tsx: 'TSX', jsx: 'JSX', py: 'Python', python: 'Python', sh: 'Shell', bash: 'Bash',
  html: 'HTML', xml: 'XML', css: 'CSS', json: 'JSON', sql: 'SQL', yaml: 'YAML', yml: 'YAML',
  md: 'Markdown', go: 'Go', rust: 'Rust', java: 'Java', php: 'PHP', c: 'C', cpp: 'C++',
};
const EXT: Record<string, { ext: string; mime: string; name?: string }> = {
  HTML: { ext: 'html', mime: 'text/html', name: 'index' }, CSS: { ext: 'css', mime: 'text/css' },
  JavaScript: { ext: 'js', mime: 'text/javascript' }, TypeScript: { ext: 'ts', mime: 'text/plain' },
  TSX: { ext: 'tsx', mime: 'text/plain' }, JSX: { ext: 'jsx', mime: 'text/plain' },
  Python: { ext: 'py', mime: 'text/x-python' }, JSON: { ext: 'json', mime: 'application/json' },
  SQL: { ext: 'sql', mime: 'text/plain' }, YAML: { ext: 'yaml', mime: 'text/yaml' },
  Shell: { ext: 'sh', mime: 'text/x-sh' }, Bash: { ext: 'sh', mime: 'text/x-sh' },
  Markdown: { ext: 'md', mime: 'text/markdown' }, XML: { ext: 'xml', mime: 'application/xml' },
  Go: { ext: 'go', mime: 'text/plain' }, Rust: { ext: 'rs', mime: 'text/plain' },
  Java: { ext: 'java', mime: 'text/plain' }, PHP: { ext: 'php', mime: 'text/plain' },
  C: { ext: 'c', mime: 'text/plain' }, 'C++': { ext: 'cpp', mime: 'text/plain' },
};

function detectLang(className: string | undefined, code: string): { label: string; isHtml: boolean } {
  const m = /language-([\w+-]+)/.exec(className || '');
  let raw = (m?.[1] || '').toLowerCase();
  if (!raw) {
    const c = code.trim().toLowerCase();
    if (/^<!doctype html|^<html[\s>]|<\/html>|<head[\s>]|<body[\s>]/.test(c)) raw = 'html';
    else if (/^\s*[{[]/.test(code) && /["}\]]$/.test(code.trim())) raw = 'json';
    else if (/^\s*(select|insert|update|delete|create|alter)\s/i.test(code)) raw = 'sql';
  }
  const isHtml = raw === 'html';
  return { label: raw ? (LANG_MAP[raw] || raw.toUpperCase()) : 'Текст', isHtml };
}

function HtmlPreview({ code, onClose }: { code: string; onClose: () => void }) {
  return createPortal(
    <div className="rf-htmlprev">
      <div className="rf-htmlprev-bar">
        <span className="rf-htmlprev-title">Просмотр HTML</span>
        <button className="rf-htmlprev-btn" onClick={onClose} aria-label="Закрыть"><X /></button>
      </div>
      <iframe title="HTML preview" sandbox="allow-scripts allow-forms allow-popups" srcDoc={code} />
    </div>,
    document.body,
  );
}

function CodeBlock({ className, code, conversationId }: { className?: string; code: string; conversationId?: string | null }) {
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const { label, isHtml } = detectLang(className, code);
  const spec = EXT[label] || { ext: 'txt', mime: 'text/plain' };
  const filename = `${spec.name || 'code'}.${spec.ext}`;

  const doCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1400);
    }).catch(() => {});
  };

  // Локальное скачивание блоба (десктоп-фоллбэк).
  const blobDownload = () => {
    const blob = new Blob([code], { type: `${spec.mime};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.target = '_blank';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  // На телефоне blob-ссылки не качаются в Telegram WebView. Материализуем код в
  // файл на сервере (публичный URL с токеном) и качаем нативно/через openLink.
  const doDownload = async () => {
    const tg = window.Telegram?.WebApp;
    const hasTg = !!(tg && (tg.downloadFile || tg.openLink));
    if (!conversationId || !hasTg) { blobDownload(); return; }
    setBusy(true);
    try {
      const file = new File([code], filename, { type: spec.mime });
      const r = await uploadFile(conversationId, file);
      let url = r.downloadUrl;
      if (!/^https?:/i.test(url)) {
        const base = (window.__API_BASE__ || import.meta.env.VITE_API_BASE_URL || location.origin).replace(/\/+$/, '');
        url = base + url;
      }
      if (tg.downloadFile) tg.downloadFile({ url, file_name: filename });
      else tg.openLink(url);
    } catch {
      blobDownload();
    } finally { setBusy(false); }
  };

  return (
    <div className="rf-code">
      <div className="rf-code-head">
        <span className="rf-code-lang">{label}</span>
        <div className="rf-code-head-actions">
          {isHtml && (
            <button className="rf-code-icon" onClick={() => setPreview(true)} type="button" title="Просмотр"><Eye /></button>
          )}
          <button className="rf-code-icon" onClick={doDownload} type="button" title="Скачать" disabled={busy}>
            {busy ? <Loader2 className="spin" /> : <Download />}
          </button>
          <button className="rf-code-icon" onClick={doCopy} type="button" title="Копировать">
            {copied ? <Check /> : <Copy />}
          </button>
        </div>
      </div>
      <pre><code className={className}>{code}</code></pre>
      {preview && <HtmlPreview code={code} onClose={() => setPreview(false)} />}
    </div>
  );
}

/** Рендерит markdown-ответ агента: жирный, списки, заголовки, таблицы, код-блоки. */
export function Markdown({ text, conversationId }: { text: string; conversationId?: string | null }) {
  return (
    <div className="rf-md">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          pre({ children }: any) { return <>{children}</>; },
          code({ inline, className, children, ...props }: any) {
            if (inline) return <code className="rf-code-inline" {...props}>{children}</code>;
            return <CodeBlock className={className} code={String(children ?? '').replace(/\n$/, '')} conversationId={conversationId} />;
          },
          a({ href, children }: any) { return <a href={href} target="_blank" rel="noreferrer">{children}</a>; },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
